import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import { CenterCounselingAiActions } from "./CenterAiWriting";

type Tab="CLASSES"|"SCHEDULE"|"COUNSELING"|"JOBS";

type AcademicYear={academicYearId:string;yearCode:string;status:string};
type Staff={personId:string;displayName:string;roles:string[]};
type ClassStaff={assignmentId:string;personId:string;displayName:string;roleType:string;status:string};
type ClassGroup={
  classGroupId:string;
  gradeLevel:string;
  classLabel:string;
  name:string;
  capacity?:number|null;
  status:string;
  studentCount:number;
  staff:ClassStaff[];
};
type Student={
  learnerId:string;
  enrollmentId:string;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
  classGroupId?:string|null;
  className?:string|null;
  openCounselingCount:number;
  lastCounselingAt?:string|null;
  nextFollowUpAt?:string|null;
};
type Counseling={
  sessionId:string;
  caseId:string;
  learnerId:string;
  displayName:string;
  caseStatus:string;
  caseType:string;
  priority:string;
  title:string;
  sessionAt:string;
  mode:string;
  sessionStatus:string;
  topic:string;
  notes?:string|null;
  nextAction?:string|null;
  nextFollowUpAt?:string|null;
  counselorName:string;
};
type ScheduleItem={
  sessionId:string;caseId:string;learnerId:string;displayName:string;sessionAt:string;
  mode:string;topic:string;caseType:string;priority:string;counselorName:string;isOverdue:boolean;
};
type FollowUpItem={
  sessionId:string;learnerId:string;displayName:string;nextFollowUpAt:string;
  topic:string;nextAction?:string|null;counselorName:string;
};
type ScheduleWorkspace={upcoming:ScheduleItem[];dueFollowUps:FollowUpItem[]};
type BulkJob={
  jobId:string;
  jobType:string;
  status:string;
  requestedCount:number;
  succeededCount:number;
  skippedCount:number;
  failedCount:number;
  createdAt:string;
};
type Workspace={
  canManage:boolean;
  academicYearId?:string|null;
  academicYears:AcademicYear[];
  staff:Staff[];
  classes:ClassGroup[];
  students:Student[];
  counseling:Counseling[];
  bulkJobs:BulkJob[];
};

function nav(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
function date(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleDateString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit"});
}
function roleLabel(value:string){
  const map:Record<string,string>={
    HOMEROOM:"담임",
    CAREER_TEACHER:"진로교사",
    COUNSELOR:"상담교사"
  };
  return map[value] || value;
}
function jobTypeLabel(value:string){
  if(value==="BADGE_AWARD")return "배지 대량발급";
  if(value==="ASSESSMENT_ASSIGNMENT")return "검사 일괄배정";
  return "학급 일괄배정";
}
function caseTypeLabel(value:string){
  const map:Record<string,string>={
    CAREER:"진로상담",
    ASSESSMENT:"검사해석",
    TRANSITION:"전환상담",
    FOLLOW_UP:"후속상담",
    OTHER:"기타"
  };
  return map[value] || value;
}

export function CenterClassCounselingPage({
  client,schoolId
}:{client:PlatformClient;schoolId:string}){
  const initialParams=new URLSearchParams(window.location.search);
  const [data,setData]=useState<Workspace|null>(null);
  const [scheduleData,setScheduleData]=useState<ScheduleWorkspace|null>(null);
  const [tab,setTab]=useState<Tab>(initialParams.get("tab")==="counseling" ? "COUNSELING" : "CLASSES");
  const [academicYearId,setAcademicYearId]=useState("");
  const [selectedClassId,setSelectedClassId]=useState("");
  const [selectedLearners,setSelectedLearners]=useState<string[]>([]);
  const [studentQuery,setStudentQuery]=useState("");
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  const [newGrade,setNewGrade]=useState("");
  const [newClass,setNewClass]=useState("");
  const [newName,setNewName]=useState("");
  const [newCapacity,setNewCapacity]=useState("30");

  const [staffPerson,setStaffPerson]=useState("");
  const [staffRole,setStaffRole]=useState("CAREER_TEACHER");

  const [counselLearner,setCounselLearner]=useState(initialParams.get("student") || "");
  const [topic,setTopic]=useState("");
  const [notes,setNotes]=useState("");
  const [caseType,setCaseType]=useState("CAREER");
  const [priority,setPriority]=useState("NORMAL");
  const [mode,setMode]=useState("IN_PERSON");
  const [nextAction,setNextAction]=useState("");
  const [followUp,setFollowUp]=useState("");
  const [scheduledAt,setScheduledAt]=useState("");
  const [selectedScheduleId,setSelectedScheduleId]=useState("");
  const [scheduleNotes,setScheduleNotes]=useState("");
  const [scheduleNextAction,setScheduleNextAction]=useState("");
  const [scheduleFollowUp,setScheduleFollowUp]=useState("");
  const [jobDetail,setJobDetail]=useState<any|null>(null);

  useEffect(()=>{
    let active=true;
    setError("");
    Promise.all([
      client.rpc("center_class_counseling_workspace",{
        p_school_id:schoolId,
        p_academic_year_id:academicYearId || null
      }),
      client.rpc("center_counseling_schedule_workspace",{p_school_id:schoolId})
    ]).then(([workspaceResult,scheduleResult])=>{
      if(!active)return;
      const firstError=workspaceResult.error || scheduleResult.error;
      if(firstError){setError(firstError.message);return;}
      const next=workspaceResult.data as Workspace;
      setData(next);
      setScheduleData(scheduleResult.data as ScheduleWorkspace);
      if(!academicYearId && next.academicYearId)setAcademicYearId(next.academicYearId);
      setSelectedClassId((current)=>
        next.classes.some((item)=>item.classGroupId===current)
          ? current
          : next.classes[0]?.classGroupId || ""
      );
      setCounselLearner((current)=>
        next.students.some((item)=>item.learnerId===current)
          ? current
          : next.students[0]?.learnerId || ""
      );
      setStaffPerson((current)=>
        next.staff.some((item)=>item.personId===current)
          ? current
          : next.staff[0]?.personId || ""
      );
    });
    return()=>{active=false;};
  },[client,schoolId,academicYearId,refresh]);

  const selectedClass=useMemo(
    ()=>data?.classes.find((item)=>item.classGroupId===selectedClassId) || null,
    [data,selectedClassId]
  );

  const classStudents=useMemo(
    ()=>data?.students.filter((item)=>item.classGroupId===selectedClassId) || [],
    [data,selectedClassId]
  );

  const candidateStudents=useMemo(()=>{
    const q=studentQuery.trim().toLowerCase();
    return (data?.students || []).filter((item)=>{
      if(item.classGroupId===selectedClassId)return false;
      if(!q)return true;
      return [item.displayName,item.gradeLevel,item.classLabel,item.className]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  },[data,selectedClassId,studentQuery]);

  const counselingStudent=useMemo(
    ()=>data?.students.find((item)=>item.learnerId===counselLearner) || null,
    [data,counselLearner]
  );

  const followUps=useMemo(
    ()=>(data?.students || []).filter((item)=>item.nextFollowUpAt).sort((a,b)=>
      String(a.nextFollowUpAt).localeCompare(String(b.nextFollowUpAt))
    ),
    [data]
  );

  async function run(key:string,fn:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,success:string){
    setBusy(key);setError("");setMessage("");
    try{
      const result=await fn();
      if(result.error)throw new Error(result.error.message);
      setMessage(success);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error ? err.message : "처리 중 오류가 발생했습니다.");
      return null;
    }finally{setBusy("");}
  }

  async function createClass(){
    const result=await run("class-create",()=>client.rpc("center_create_class_group",{
      p_school_id:schoolId,
      p_academic_year_id:academicYearId,
      p_grade_level:newGrade,
      p_class_label:newClass,
      p_name:newName.trim() || null,
      p_capacity:newCapacity ? Number(newCapacity) : null
    }),"학급 Master를 저장했습니다.");
    if(result){
      setSelectedClassId(String(result));
      setNewGrade("");setNewClass("");setNewName("");
    }
  }

  async function assignStaff(){
    if(!selectedClass || !staffPerson)return;
    await run("staff",()=>client.rpc("center_assign_class_staff",{
      p_school_id:schoolId,
      p_class_group_id:selectedClass.classGroupId,
      p_person_id:staffPerson,
      p_role_type:staffRole
    }),"담당교사를 배정하고 학생 접근권한을 동기화했습니다.");
  }

  async function moveStudents(){
    if(!selectedClass || !selectedLearners.length)return;
    const result=await run("move",()=>client.rpc("center_bulk_move_students_to_class",{
      p_school_id:schoolId,
      p_class_group_id:selectedClass.classGroupId,
      p_learner_ids:selectedLearners
    }),"선택 학생의 학급배정을 처리했습니다.");
    if(result)setSelectedLearners([]);
  }

  async function scheduleCounseling(){
    if(!counselLearner || !topic.trim() || !scheduledAt)return;
    const result=await run("schedule",()=>client.rpc("center_schedule_counseling",{
      p_school_id:schoolId,p_learner_id:counselLearner,
      p_session_at:new Date(scheduledAt).toISOString(),
      p_topic:topic.trim(),p_case_type:caseType,p_priority:priority,p_mode:mode
    }),"상담 일정을 등록했습니다.");
    if(result){setTopic("");setScheduledAt("");}
  }

  async function completeScheduled(){
    if(!selectedScheduleId)return;
    const result=await run("schedule-complete",()=>client.rpc("center_complete_scheduled_counseling",{
      p_school_id:schoolId,p_session_id:selectedScheduleId,
      p_notes:scheduleNotes.trim() || null,
      p_next_action:scheduleNextAction.trim() || null,
      p_next_follow_up_at:scheduleFollowUp ? new Date(scheduleFollowUp).toISOString() : null
    }),"예약 상담을 완료 처리했습니다.");
    if(result){setSelectedScheduleId("");setScheduleNotes("");setScheduleNextAction("");setScheduleFollowUp("");}
  }

  async function loadJob(jobId:string){
    setError("");
    const {data,error}=await client.rpc("center_bulk_job_detail",{p_school_id:schoolId,p_job_id:jobId});
    if(error){setError(error.message);return;}
    setJobDetail(data);
  }

  async function retryJob(jobId:string){
    await run("job-retry",()=>client.rpc("center_retry_bulk_job",{p_school_id:schoolId,p_job_id:jobId}),"실패 항목만 새 Batch Job으로 재처리했습니다.");
    setJobDetail(null);
  }

  async function logCounseling(){
    if(!counselLearner || !topic.trim())return;
    const followUpIso=followUp ? new Date(followUp).toISOString() : null;
    const result=await run("counsel",()=>client.rpc("center_log_counseling",{
      p_school_id:schoolId,
      p_learner_id:counselLearner,
      p_topic:topic.trim(),
      p_notes:notes.trim() || null,
      p_case_type:caseType,
      p_priority:priority,
      p_mode:mode,
      p_next_action:nextAction.trim() || null,
      p_next_follow_up_at:followUpIso
    }),"상담기록과 후속조치를 학생 Master에 저장했습니다.");
    if(result){
      setTopic("");setNotes("");setNextAction("");setFollowUp("");
    }
  }

  if(!data || !scheduleData)return <p className="muted">학급 ERP를 불러오는 중…</p>;

  return (
    <section className="class-erp-page">
      <header className="class-erp-head">
        <div>
          <p className="eyebrow">Class & Counseling ERP</p>
          <h2>학급·상담</h2>
          <p>학급편성, 담당교사, 학생 상담과 일괄작업을 Student Master 기준으로 관리합니다.</p>
        </div>
        <select value={academicYearId} onChange={(e)=>setAcademicYearId(e.target.value)}>
          {data.academicYears.map((year)=>(
            <option key={year.academicYearId} value={year.academicYearId}>
              {year.yearCode} · {year.status==="ACTIVE" ? "현재" : year.status}
            </option>
          ))}
        </select>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="class-erp-metrics">
        <Metric label="학급" value={data.classes.length} />
        <Metric label="학생" value={data.students.length} />
        <Metric label="담당교사 배정" value={data.classes.reduce((sum,item)=>sum+item.staff.length,0)} />
        <Metric label="상담 Case" value={data.students.reduce((sum,item)=>sum+Number(item.openCounselingCount || 0),0)} />
        <Metric label="후속상담 예정" value={followUps.length} />
      </div>

      <nav className="class-erp-tabs">
        <button className={tab==="CLASSES"?"active":""} onClick={()=>setTab("CLASSES")}>학급편성·담당교사</button>
        <button className={tab==="SCHEDULE"?"active":""} onClick={()=>setTab("SCHEDULE")}>상담일정</button>
        <button className={tab==="COUNSELING"?"active":""} onClick={()=>setTab("COUNSELING")}>상담원장</button>
        <button className={tab==="JOBS"?"active":""} onClick={()=>setTab("JOBS")}>일괄작업</button>
      </nav>

      {tab==="CLASSES" && (
        <div className="class-master-layout">
          <aside className="class-list-panel">
            <div className="panel-title"><strong>Class Master</strong><span>{data.classes.length}개 학급</span></div>
            <div className="class-list">
              {data.classes.map((item)=>(
                <button key={item.classGroupId} className={selectedClassId===item.classGroupId?"active":""} onClick={()=>{setSelectedClassId(item.classGroupId);setSelectedLearners([]);}}>
                  <span><strong>{item.name}</strong><small>{item.studentCount}명 · 정원 {item.capacity || "-"}</small></span>
                  <b>{item.staff.length}명</b>
                </button>
              ))}
            </div>

            {data.canManage && (
              <details className="teacher-setup-details class-create-details">
                <summary><span><strong>학급 추가</strong><small>새 반 개설</small></span><b>열기</b></summary>
                <div className="class-create-form">
                  <input type="number" min="1" max="12" value={newGrade} onChange={(e)=>setNewGrade(e.target.value)} placeholder="학년" />
                  <input value={newClass} onChange={(e)=>setNewClass(e.target.value)} placeholder="반" />
                  <input value={newName} onChange={(e)=>setNewName(e.target.value)} placeholder="학급명(선택)" />
                  <input type="number" min="1" value={newCapacity} onChange={(e)=>setNewCapacity(e.target.value)} placeholder="정원" />
                  <button className="primary" disabled={busy!=="" || !newGrade || !newClass} onClick={createClass}>학급 저장</button>
                </div>
              </details>
            )}
          </aside>

          <div className="class-detail-panel">
            {selectedClass ? (
              <>
                <header className="class-selected-head">
                  <div>
                    <p className="eyebrow">{selectedClass.gradeLevel}학년</p>
                    <h3>{selectedClass.name}</h3>
                    <p>{selectedClass.studentCount}명 · 담당교사 {selectedClass.staff.length}명</p>
                  </div>
                  <div className="class-head-actions">
                    <button className="secondary" onClick={()=>nav(`/assessments?grade=${encodeURIComponent(selectedClass.gradeLevel)}&class=${encodeURIComponent(selectedClass.classLabel)}`)}>
                      이 반 진로검사
                    </button>
                    <button className="secondary" onClick={()=>nav(`/badges?grade=${encodeURIComponent(selectedClass.gradeLevel)}&class=${encodeURIComponent(selectedClass.classLabel)}`)}>
                      이 반 디지털배지
                    </button>
                  </div>
                </header>

                <section className="class-erp-card">
                  <div className="panel-title"><strong>담당교사</strong><span>배정 시 해당 학급 학생 접근권한이 자동 연결됩니다.</span></div>
                  <div className="class-staff-chips">
                    {selectedClass.staff.map((item)=>(
                      <span key={item.assignmentId}>
                        <b>{roleLabel(item.roleType)}</b> {item.displayName}
                        {data.canManage && <button onClick={()=>run("staff-end",()=>client.rpc("center_end_class_staff",{p_school_id:schoolId,p_assignment_id:item.assignmentId}),"담당교사 배정을 종료했습니다.")}>×</button>}
                      </span>
                    ))}
                    {!selectedClass.staff.length && <small>아직 담당교사가 없습니다.</small>}
                  </div>
                  {data.canManage && (
                    <div className="class-staff-form">
                      <select value={staffPerson} onChange={(e)=>setStaffPerson(e.target.value)}>
                        {data.staff.map((item)=><option key={item.personId} value={item.personId}>{item.displayName}</option>)}
                      </select>
                      <select value={staffRole} onChange={(e)=>setStaffRole(e.target.value)}>
                        <option value="HOMEROOM">담임</option>
                        <option value="CAREER_TEACHER">진로교사</option>
                        <option value="COUNSELOR">상담교사</option>
                      </select>
                      <button className="primary" disabled={busy!=="" || !staffPerson} onClick={assignStaff}>담당 배정</button>
                    </div>
                  )}
                </section>

                <section className="class-erp-card">
                  <div className="panel-title"><strong>현재 학생</strong><span>{classStudents.length}명</span></div>
                  <div className="class-roster">
                    {classStudents.map((student)=>(
                      <div key={student.learnerId}>
                        <span><strong>{student.displayName}</strong><small>상담 Case {student.openCounselingCount} · 최근상담 {date(student.lastCounselingAt)}</small></span>
                        <button onClick={()=>{setCounselLearner(student.learnerId);setTab("COUNSELING");}}>상담</button>
                      </div>
                    ))}
                    {!classStudents.length && <p className="case-empty">배정된 학생이 없습니다.</p>}
                  </div>
                </section>

                {data.canManage && (
                  <section className="class-erp-card">
                    <div className="panel-title"><strong>학생 일괄배정</strong><span>다른 반 또는 미배정 학생을 이 반으로 이동합니다.</span></div>
                    <input className="teacher-search" value={studentQuery} onChange={(e)=>setStudentQuery(e.target.value)} placeholder="학생 검색" />
                    <div className="class-candidate-list">
                      {candidateStudents.map((student)=>(
                        <label key={student.learnerId}>
                          <input type="checkbox" checked={selectedLearners.includes(student.learnerId)} onChange={()=>{
                            setSelectedLearners((current)=>current.includes(student.learnerId)?current.filter((id)=>id!==student.learnerId):[...current,student.learnerId]);
                          }} />
                          <span><strong>{student.displayName}</strong><small>{student.gradeLevel || "-"}학년 · {student.className || "미배정"}</small></span>
                        </label>
                      ))}
                    </div>
                    <button className="primary class-bulk-button" disabled={busy!=="" || !selectedLearners.length} onClick={moveStudents}>
                      선택 {selectedLearners.length}명 → {selectedClass.name} 배정
                    </button>
                  </section>
                )}
              </>
            ) : <p className="case-empty">학급을 선택하세요.</p>}
          </div>
        </div>
      )}

      {tab==="SCHEDULE" && (
        <div className="schedule-erp-layout">
          <section className="counseling-entry-card">
            <div className="panel-title"><strong>상담 예약</strong><span>미래 일정으로 등록하고 상담 후 기록을 완성합니다.</span></div>
            <label>학생<select value={counselLearner} onChange={(e)=>setCounselLearner(e.target.value)}>{data.students.map((student)=><option key={student.learnerId} value={student.learnerId}>{student.displayName} · {student.className || "학급 미정"}</option>)}</select></label>
            <div className="counseling-form-grid">
              <label>상담유형<select value={caseType} onChange={(e)=>setCaseType(e.target.value)}><option value="CAREER">진로상담</option><option value="ASSESSMENT">검사해석</option><option value="TRANSITION">전환상담</option><option value="FOLLOW_UP">후속상담</option><option value="OTHER">기타</option></select></label>
              <label>중요도<select value={priority} onChange={(e)=>setPriority(e.target.value)}><option value="NORMAL">일반</option><option value="ATTENTION">관심</option><option value="HIGH">우선</option></select></label>
              <label>상담방식<select value={mode} onChange={(e)=>setMode(e.target.value)}><option value="IN_PERSON">대면</option><option value="PHONE">전화</option><option value="VIDEO">화상</option><option value="MESSAGE">메시지</option><option value="OTHER">기타</option></select></label>
              <label className="wide">상담 주제<input value={topic} onChange={(e)=>setTopic(e.target.value)} placeholder="예: 진로검사 결과 해석" /></label>
              <label className="wide">상담 일시<input type="datetime-local" value={scheduledAt} onChange={(e)=>setScheduledAt(e.target.value)} /></label>
            </div>
            <button className="primary" disabled={busy!=="" || !counselLearner || !topic.trim() || !scheduledAt} onClick={scheduleCounseling}>상담 예약</button>
          </section>

          <section className="schedule-list-card">
            <div className="panel-title"><strong>예정·미처리 상담</strong><span>{scheduleData.upcoming.length}건</span></div>
            <div className="schedule-list">
              {scheduleData.upcoming.map((item)=>(
                <article key={item.sessionId} className={item.isOverdue?"overdue":""}>
                  <div><strong>{item.displayName} · {item.topic}</strong><span>{new Date(item.sessionAt).toLocaleString("ko-KR")} · {caseTypeLabel(item.caseType)} · {item.counselorName}</span>{item.isOverdue && <small>예정시간 경과 · 완료 또는 취소 처리가 필요합니다.</small>}</div>
                  <div className="case-actions">
                    <button onClick={()=>setSelectedScheduleId(item.sessionId)}>완료 기록</button>
                    <button className="secondary" disabled={busy!==""} onClick={()=>run("schedule-cancel",()=>client.rpc("center_cancel_scheduled_counseling",{p_school_id:schoolId,p_session_id:item.sessionId}),"상담 일정을 취소했습니다.")}>취소</button>
                  </div>
                </article>
              ))}
              {!scheduleData.upcoming.length && <p className="case-empty">예약된 상담이 없습니다.</p>}
            </div>

            {selectedScheduleId && (
              <div className="schedule-complete-form">
                <strong>상담 완료 기록</strong>
                <textarea rows={4} value={scheduleNotes} onChange={(e)=>setScheduleNotes(e.target.value)} placeholder="상담 내용" />
                <input value={scheduleNextAction} onChange={(e)=>setScheduleNextAction(e.target.value)} placeholder="다음 조치" />
                <label>후속상담 예정<input type="datetime-local" value={scheduleFollowUp} onChange={(e)=>setScheduleFollowUp(e.target.value)} /></label>
                <button className="primary" disabled={busy!==""} onClick={completeScheduled}>완료 저장</button>
              </div>
            )}
          </section>

          <section className="schedule-followup-card">
            <div className="panel-title"><strong>14일 이내 후속조치</strong><span>{scheduleData.dueFollowUps.length}건</span></div>
            <div className="followup-list">
              {scheduleData.dueFollowUps.map((item)=>(
                <button key={item.sessionId} onClick={()=>{setCounselLearner(item.learnerId);setTab("COUNSELING");}}>
                  <span><strong>{item.displayName}</strong><small>{item.topic}{item.nextAction ? " · "+item.nextAction : ""}</small></span>
                  <b>{date(item.nextFollowUpAt)}</b>
                </button>
              ))}
              {!scheduleData.dueFollowUps.length && <p className="case-empty">14일 이내 후속상담 대상이 없습니다.</p>}
            </div>
          </section>
        </div>
      )}

      {tab==="COUNSELING" && (
        <div className="counseling-erp-layout">
          <section className="counseling-entry-card">
            <div className="panel-title"><strong>상담 기록</strong><span>학생별 Case와 후속조치가 누적됩니다.</span></div>
            <label>학생
              <select value={counselLearner} onChange={(e)=>setCounselLearner(e.target.value)}>
                {data.students.map((student)=><option key={student.learnerId} value={student.learnerId}>{student.displayName} · {student.className || "학급 미정"}</option>)}
              </select>
            </label>
            {counselingStudent && (
              <div className="counseling-student-strip">
                <span><strong>{counselingStudent.displayName}</strong><small>{counselingStudent.className || "학급 미정"}</small></span>
                <span>열린 Case <b>{counselingStudent.openCounselingCount}</b></span>
                <span>최근상담 <b>{date(counselingStudent.lastCounselingAt)}</b></span>
              </div>
            )}
            <div className="counseling-form-grid">
              <label>상담유형<select value={caseType} onChange={(e)=>setCaseType(e.target.value)}>
                <option value="CAREER">진로상담</option><option value="ASSESSMENT">검사해석</option><option value="TRANSITION">전환상담</option><option value="FOLLOW_UP">후속상담</option><option value="OTHER">기타</option>
              </select></label>
              <label>중요도<select value={priority} onChange={(e)=>setPriority(e.target.value)}>
                <option value="NORMAL">일반</option><option value="ATTENTION">관심</option><option value="HIGH">우선</option>
              </select></label>
              <label>상담방식<select value={mode} onChange={(e)=>setMode(e.target.value)}>
                <option value="IN_PERSON">대면</option><option value="PHONE">전화</option><option value="VIDEO">화상</option><option value="MESSAGE">메시지</option><option value="OTHER">기타</option>
              </select></label>
              <label className="wide">상담 주제<input value={topic} onChange={(e)=>setTopic(e.target.value)} placeholder="예: 진로검사 결과 해석 및 선택과목 상담" /></label>
              <label className="wide">상담 기록<textarea value={notes} onChange={(e)=>setNotes(e.target.value)} rows={5} placeholder="상담 내용과 학생의 선택을 기록합니다." /></label>
              <label className="wide">다음 조치<input value={nextAction} onChange={(e)=>setNextAction(e.target.value)} placeholder="예: 관련 학과 탐색자료 전달" /></label>
              <label>후속상담 예정<input type="datetime-local" value={followUp} onChange={(e)=>setFollowUp(e.target.value)} /></label>
              <CenterCounselingAiActions
                client={client}
                schoolId={schoolId}
                learnerId={counselLearner}
                topic={topic}
                notes={notes}
                nextAction={nextAction}
                onApply={(next)=>{setNotes(next.notes);setNextAction(next.nextAction);}}
              />
            </div>
            <button className="primary" disabled={busy!=="" || !counselLearner || !topic.trim()} onClick={logCounseling}>상담기록 저장</button>
          </section>

          <section className="counseling-ledger-card">
            <div className="panel-title"><strong>상담 원장</strong><span>최근 기록</span></div>
            <div className="counseling-ledger">
              {data.counseling.slice(0,40).map((item)=>(
                <article key={item.sessionId}>
                  <div className="counseling-ledger-head">
                    <span><strong>{item.displayName}</strong><small>{caseTypeLabel(item.caseType)} · {item.counselorName}</small></span>
                    <b>{date(item.sessionAt)}</b>
                  </div>
                  <h4>{item.topic}</h4>
                  {item.notes && <p>{item.notes}</p>}
                  {item.nextAction && <small>다음 조치 · {item.nextAction}</small>}
                  {item.nextFollowUpAt && <small className="followup">후속상담 · {date(item.nextFollowUpAt)}</small>}
                </article>
              ))}
              {!data.counseling.length && <p className="case-empty">상담기록이 없습니다.</p>}
            </div>
          </section>
        </div>
      )}

      {tab==="JOBS" && (
        <div className="bulk-job-layout">
          <section className="bulk-job-card">
            <div className="panel-title"><strong>일괄작업 이력</strong><span>실패한 학생만 다시 처리할 수 있습니다.</span></div>
            <div className="bulk-job-list">
              {data.bulkJobs.map((job)=>(
                <article key={job.jobId}>
                  <button className="job-main" onClick={()=>loadJob(job.jobId)}><span><strong>{jobTypeLabel(job.jobType)}</strong><small>{date(job.createdAt)} · {job.jobId}</small></span></button>
                  <dl><div><dt>요청</dt><dd>{job.requestedCount}</dd></div><div><dt>성공</dt><dd>{job.succeededCount}</dd></div><div><dt>실패</dt><dd>{job.failedCount}</dd></div></dl>
                  <div className="job-actions"><b className={"job-status "+job.status.toLowerCase()}>{job.status}</b>{["PARTIAL","FAILED"].includes(job.status) && <button disabled={busy!==""} onClick={()=>retryJob(job.jobId)}>실패항목 재처리</button>}</div>
                </article>
              ))}
              {!data.bulkJobs.length && <p className="case-empty">아직 일괄작업 이력이 없습니다.</p>}
            </div>
          </section>
          {jobDetail && (
            <section className="bulk-job-detail">
              <div className="panel-title"><strong>작업 상세</strong><button className="text-button" onClick={()=>setJobDetail(null)}>닫기</button></div>
              <div className="job-detail-summary"><span>상태 <b>{jobDetail.status}</b></span><span>성공 <b>{jobDetail.succeededCount}</b></span><span>실패 <b>{jobDetail.failedCount}</b></span><span>재시도 <b>{jobDetail.retryNumber || 0}</b></span></div>
              <div className="job-item-list">
                {(jobDetail.items || []).map((item:any)=><div key={item.itemId} className={item.status==="FAILED"?"failed":""}><span><strong>{item.displayName || item.learnerId || "항목"}</strong><small>{item.errorMessage || item.status}</small></span><b>{item.status}</b></div>)}
              </div>
            </section>
          )}
        </div>
      )}
    </section>
  );
}

function Metric({label,value}:{label:string;value:number}){
  return <div className="class-metric"><span>{label}</span><strong>{value}</strong></div>;
}
